import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { isEmptyValue } from '../../../lib/validation';
import FieldTypes from '../../../constants/FieldTypes';
import FieldTitle from './FieldTitle';
import FieldDecorators from './FieldDecorators';
import { StyledFieldHeader, isFieldHeaderEmpty } from './FieldHeader';
import { hexToRGBA } from '../../../lib/colors';

import FieldModes from '../../../constants/FieldModes';
import PageLayoutModes from '../../../constants/PageLayoutModes';
import FieldPresentationModes from '../../../constants/FieldPresentationModes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FormStylesOptions from '../../../constants/FormStylesOptions';

import Tooltip from '../../misc/Tooltip';

import { calculateShrinkFactor, getAutoShrinkClampStyles, getFittingMaxLines, getLineHeightPixels } from '../../../lib/autoShrinkHelper';
import {
  measureAutoHeight, createMeasurementSurrogate, asText, AUTO_HEIGHT_RETRY_DELAY_MS, AUTO_HEIGHT_MAX_RETRIES,
} from '../../../lib/autoHeightHelper';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}

  /*
   * Only use height when you don't need height measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right:0;
  top:0;
  z-index: 9999999;
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class TextareaField extends PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    value: PropTypes.string,
    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,
    height: PropTypes.number, //Important for re-calculating line heights

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,
    lineHeight: PropTypes.number,
    primaryColor: PropTypes.string,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    //Used for calculating width and height of element
    measure: PropTypes.bool,
    onMeasureCompleted: PropTypes.func,

  }

  static defaultProps = {
    lineHeight: 10
  }

  constructor(props) {

    super(props);

    const { value } = this.props;

    this.state = {
      focused: false,
      changes: value,
    };

    /**
     * Auto-height only. One request per blur: { generation, value, edited }. The generation
     * ties every deferred emission and retry to the blur that made it; the value is what was
     * blurred; edited is the permission to shrink. Same machine as TextField's.
     */
    this.autoHeightRequest = null;
    this.autoHeightGeneration = 0;
    this.autoHeightTimer = null;
    this.autoHeightRetryTimer = null;
    this.autoHeightRetries = 0;
  }

  /**
   * Record the blur as a request. A blur is edited when the value it carries differs from the
   * committed one (compared as text: a never-filled textarea has an undefined value and an
   * empty DOM string). A request already holding exactly this value is reused (the browser can
   * fire two native blurs for one loss of focus, and the second may already see the committed
   * value), keeping its generation and any permission it had. A different value opens a new
   * request and never inherits an old permission.
   *
   * @param {*} next //The value being blurred
   * @param {*} value //The committed value prop
   * @returns {Object}
   * @private
   */
  openAutoHeightRequest = (next, value) => {

    const edited = asText(next) !== asText(value);

    const current = this.autoHeightRequest;

    if (current && asText(current.value) === asText(next)) {
      current.edited = current.edited || edited;
      return current;
    }

    this.autoHeightGeneration += 1;

    this.autoHeightRequest = { generation: this.autoHeightGeneration, value: next, edited };

    return this.autoHeightRequest;

  };

  /**
   * Everywhere a request dies: a diverging value from outside, the field disabled, auto-height
   * switched off, the handler gone, unmount. Both timers go with it, the zero-delay emission
   * included, so nothing measured for the old request can reach the page.
   *
   * @private
   */
  cancelAutoHeightRequest = () => {
    clearTimeout(this.autoHeightTimer);
    clearTimeout(this.autoHeightRetryTimer);
    this.autoHeightTimer = null;
    this.autoHeightRetryTimer = null;
    this.autoHeightRequest = null;
    this.autoHeightRetries = 0;
  };

  /**
   * Send one measurement and act on the page's answer. 'applied' and 'settled' spend the
   * request. 'retry' means the box was still animating, or the document moved under the
   * measurement: measure again once react-grid-layout's transition has finished, from a
   * surrogate of the editor (retryAutoHeight). Three attempts, backing off, then the request is
   * left for the next blur to reuse or replace.
   *
   * @param {Object} measurement //From measureAutoHeight, stamped with the request's generation
   * @private
   */
  emitAutoHeight = (measurement) => {

    const request = this.autoHeightRequest;

    //Cancelled between the blur and this timer, or replaced by a newer blur: never reaches the page
    if (!request || request.generation !== measurement.generation) return;

    const { onAutoHeightChange, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (!onAutoHeightChange) {
      this.cancelAutoHeightRequest();
      return;
    }

    const result = onAutoHeightChange(fieldId, fieldIdentifier, fieldPositionId, measurement);

    if (result !== 'retry') {
      if (this.autoHeightRequest === request) this.autoHeightRequest = null;
      this.autoHeightRetries = 0;
      return;
    }

    if (this.autoHeightRetries >= AUTO_HEIGHT_MAX_RETRIES) return;

    this.autoHeightRetries += 1;

    clearTimeout(this.autoHeightRetryTimer);

    //Backs off (250, 500, 1000ms): a throttled tab or a slow frame must not exhaust the attempts inside one animation
    const delay = AUTO_HEIGHT_RETRY_DELAY_MS * (2 ** (this.autoHeightRetries - 1));

    this.autoHeightRetryTimer = setTimeout(() => this.retryAutoHeight(request.generation), delay);

  };

  /**
   * Measure again after the box has settled. The editor may be gone by now (auto-shrink swaps
   * it for a clamped display), so the text is read from a hidden textarea that wraps like it,
   * while the box facts come from the pinned display node, found by the id every display shape
   * labels it with (StandardTextarea, FillablePDFTextarea, AutoShrinkTextareaDisplay). The
   * value is the request's own, not the prop: a controlled host may not have committed it yet,
   * and a foreign value cancels the request in componentDidUpdate.
   *
   * The pitch is the editor's own rule, recomputed against the settled box: a fillable PDF
   * textarea draws at wrapper height x lineHeight / 100 (FillablePDFTextarea), anything else at
   * the line-height of the effective font.
   *
   * @param {Number} generation //The request this retry belongs to
   * @private
   */
  retryAutoHeight = (generation) => {

    const request = this.autoHeightRequest;

    if (!request || request.generation !== generation) return;

    const { height, disabled, autoHeightEnabled, fontSize, lineHeight, fieldPositionId } = this.props;
    const { focused, wrapperRef } = this.state;

    const box = wrapperRef ? wrapperRef.querySelector(`[data-testid="${fieldPositionId}-textarea-field"]`) : null;

    //Focused again: the editor is up and its own blur will measure
    if (focused || disabled || !autoHeightEnabled || !box) return;

    const { isFillablePDFForm } = this.getExporterFlags();

    const slot = box.parentElement;

    const fontPx = parseFloat(window.getComputedStyle(box).fontSize) || fontSize;

    const lineHeightPx = (isFillablePDFForm && slot)
      ? slot.offsetHeight * ((lineHeight || 10) / 100)
      : getLineHeightPixels(fontPx);

    const surrogate = createMeasurementSurrogate(box, { lineHeightPx, value: request.value });

    if (!surrogate) return;

    let measurement = null;

    try {
      measurement = measureAutoHeight(box, request.value, FieldTypes.textarea, height, { edited: request.edited, contentNode: surrogate });
    } finally {
      surrogate.remove();
    }

    if (!measurement) return;

    measurement.generation = request.generation;

    this.emitAutoHeight(measurement);

  };

  componentDidUpdate(prevProps) {

    const { changes, wrapperRef } = this.state;

    const {
      value, fieldId, fieldIdentifier, fieldPositionId, onMeasureCompleted, measuredHeight, measuredOn, updatedOn, height,
      disabled, autoHeightEnabled,
    } = this.props;

    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });

    /**
     * A request outlives the blur that made it (deferred emission, retries), so anything that
     * makes its measurement meaningless cancels it: a value from outside that is not the one
     * blurred (the host committing that same value is fine), the field disabled, or auto-height
     * switched off. Runs before the zero-delay emission fires, so a synchronous update lands
     * here first.
     */
    const request = this.autoHeightRequest;
    if (request) {
      const valueDiverged = value !== prevProps.value && asText(value) !== asText(request.value);
      const switchedOff = (disabled && !prevProps.disabled) || (!autoHeightEnabled && prevProps.autoHeightEnabled);
      if (valueDiverged || switchedOff) this.cancelAutoHeightRequest();
    }

    const remeasureRequired = !measuredHeight || (measuredOn && updatedOn && measuredOn < updatedOn);

    const { isExporterExpandable, isAutoHeightMeasurementPass } = this.getExporterFlags();

    //Same test as the exporter's float whitelist, so the fields that report are the fields the page waits for
    const hasPopulatedValue = !isEmptyValue(value, FieldTypes.textarea);

    //Important Note: Vanilla JS initally has a height of 0. We need to ensure there is a height before we can measure. This is why we check for the bounding client rect.
    const refHeightAvailable = wrapperRef && wrapperRef.getBoundingClientRect() && wrapperRef.getBoundingClientRect().height;

    if (wrapperRef && isAutoHeightMeasurementPass && hasPopulatedValue && refHeightAvailable && remeasureRequired) {

      /**
       * Auto-height on a float page: the box is kept and the overflow is reported, the same two
       * numbers a blur records.
       *
       * Inside the frame callback on purpose. FillablePDFTextarea only learns its ruled pitch
       * in its own componentDidUpdate, one render after its wrapper ref lands, and until then
       * the text is drawn at a 0px line height and would measure as fitting. React has flushed
       * that render well before the next frame.
       *
       * All three display shapes this field can take here label their display node with the
       * field's own id (StandardTextarea, FillablePDFTextarea, AutoShrinkTextareaDisplay), so
       * the node is found by that attribute rather than threading a fourth ref through them.
       * ImageField walks its [data-image-wrapper] nodes the same way.
       */
      requestAnimationFrame(() => {

        const node = wrapperRef.querySelector(`[data-testid="${fieldPositionId}-textarea-field"]`);

        const measurement = measureAutoHeight(node, value, FieldTypes.textarea, height);

        if (!measurement) return;

        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, {

          //Today's reading (scrollHeight less clientHeight): the exporter grows only, on the same pixel as before
          autoHeightOverflowPx: measurement.legacyOverflowPx,
          autoHeightBoxPx: measurement.boxHeightPx,
          measuredOn: new Date().getTime(),
        });

      });

      return;

    }

    if (wrapperRef && isExporterExpandable && onMeasureCompleted && hasPopulatedValue && refHeightAvailable && remeasureRequired) {
      requestAnimationFrame(() => {
        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, { height: refHeightAvailable, measuredOn: new Date().getTime() });
      });
    }

  }

  /**
   * The questions the exporter needs answered about this field, resolved in one place so
   * componentDidUpdate and render can never disagree about them. See TextField.getExporterFlags
   * for the full account; in short, the exporter has two ways of making room for a value,
   * decided by the page layout, and a field is in at most one of them.
   *
   * isExporterExpandable: grid pages. The field lets go of its box and takes its natural height
   *   at full size, in BOTH exporter passes, because JoyDocExporter passes measure to both.
   *   Auto-shrink is deliberately not consulted: a printed page has to show every word, so the
   *   field grows instead. That is the fix for the ellipsized multiline value.
   *
   * isExporterAutoHeight: float pages, auto-height fields only. The field keeps its drawn box,
   *   renders exactly as it does on screen (shrunk font, no clamp) and reports its overflow.
   *   Every other field on a float page keeps its drawn box and current look. This is why
   *   expand is keyed to the layout and not to "not a fillable form": treating a fillable
   *   field as expandable collapses the wrapper to auto-height, which zeroes out the derived
   *   line-height and overlaps the wrapped text.
   *
   * displayType keeps both in step with getExporterMeasurableFields, which only admits original
   * display fields. A field drawn as a mapped table cell is never measured, so if it drew its
   * growing node anyway its text would spill out of a box that never grew.
   *
   * @private
   */
  getExporterFlags = () => {

    const { measure, onMeasureCompleted, fieldMode, pageLayoutMode, fieldPresentationMode, displayType, autoHeightEnabled } = this.props;

    //Still needed by render to pick the fillable display shape; no longer part of the export mode decision
    const isFillablePDFForm = pageLayoutMode === PageLayoutModes.float && fieldPresentationMode === FieldPresentationModes.transparent;

    //autoHeightEnabled carries the type gate and, in pdf mode, the float page gate (Field.js)
    const isExporterAutoHeight = !!measure
      && fieldMode === FieldModes.pdf
      && pageLayoutMode === PageLayoutModes.float
      && displayType === FieldDisplayTypes.original
      && !!autoHeightEnabled;

    //"Not float" rather than "grid": a page saved without a layout property is a grid page
    const isExporterExpandable = !!measure
      && fieldMode === FieldModes.pdf
      && pageLayoutMode !== PageLayoutModes.float
      && displayType === FieldDisplayTypes.original;

    return {
      isFillablePDFForm,
      isExporterExpandable,
      isExporterAutoHeight,
      isAutoHeightMeasurementPass: isExporterAutoHeight && !!onMeasureCompleted,
    };

  };

  componentWillUnmount() {

    const { value, disabled, onChange, fieldId, fieldIdentifier, fieldPositionId, onBlur } = this.props;
    const { changes, focused } = this.state;

    this.cancelAutoHeightRequest();

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value && !disabled) {

      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);

      if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

    }

  }

  render() {

    const { focused, changes, wrapperRef } = this.state;

    const {

      fieldId,
      fieldIdentifier,
      fieldPositionId,

      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      tabIndex,
      height,

      //SDK Styles
      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,
      onMeasureCompleted,
      lineHeight,

      //Action handlers
      onChange,
      onFocus,
      onBlur,
      onDecoratorClick,
      fieldMode,
      fieldPresentationMode,
      pageLayoutMode,
      decorators,

    } = this.props;

    const hasPopulatedValue = !isEmptyValue(value, FieldTypes.textarea);

    /**
     * Same flags as componentDidUpdate, resolved once because the shrink calculation and the
     * node choice below both depend on them. See getExporterFlags for what they mean.
     */
    const { isFillablePDFForm, isExporterExpandable, isExporterAutoHeight } = this.getExporterFlags();

    /**
     * Only the props that are not already destructured above. height, title, titleDisplay,
     * titleFontSize and lineHeight are all in scope already, and redeclaring any of them here
     * is a syntax error.
     */
    const {
      autoShrinkEnabled,
      autoShrinkMaxLines,
      autoShrinkMaxLength,
      autoHeightEnabled,
      onAutoHeightChange,
      baseLineHeight,
      designWidth,
      designHeight,
      fontFamily,
    } = this.props;

    /**
     * Auto-height, the opposite knob to auto-shrink: the box grows to the value instead of the
     * value being cut to the box. Field.js has already checked the field type and, in pdf mode,
     * only lets this through on a float page, so a grid export never reaches any of the
     * behaviour hanging off it.
     */
    const isAutoHeight = !!autoHeightEnabled;

    //Room taken by the field title above the input, needed by both calculations below
    const reservedHeight = (!title || titleDisplay === FormStylesOptions.displayNone)
      ? 0
      : Math.round((titleFontSize || 12) * 1.2) + 4;

    /**
     * On a fillable PDF form the ruled lines are drawn from the lineHeight percentage, so the
     * default of 10 produces 10 rules. Clamping to 2 lines under 10 drawn rules would look
     * wrong, and shrinking the font buys no extra room because the line pitch is pinned to
     * the rules. Cap the limit at however many rules are actually drawn.
     */
    /**
     * With auto-height on, the count is frozen at the pitch the field started with.
     *
     * Every change rescales lineHeight so the printed pitch stays put, which means a taller box
     * draws more rules and a shorter one fewer. Counting the rules of the moment would loosen
     * or tighten the cap below with every change, the font would follow, and the box would
     * change again on the next blur. baseLineHeight is recorded on the first change in either
     * direction (and by the settings panel), so before that lineHeight is still the base and
     * the fallback is exactly right.
     */
    const shrinkLineHeight = (isAutoHeight && Number.isFinite(baseLineHeight)) ? baseLineHeight : lineHeight;

    const ruledLineCount = Math.round(100 / (shrinkLineHeight || 10));

    let effectiveMaxLines;
    if (autoShrinkEnabled) {
      effectiveMaxLines = isFillablePDFForm ? Math.min(autoShrinkMaxLines, ruledLineCount) : autoShrinkMaxLines;
    }

    /**
     * The ruled line pitch is a percentage of the input area, not of the whole field.
     * FillablePDFTextarea measures its wrapper for this, and so does the display node below.
     * This value is the design space approximation used by the shrink calculation, which has
     * to stay deterministic and work from persisted numbers.
     */
    const approxInputAreaHeight = Math.max(1, (designHeight || 0) - reservedHeight);
    const lineHeightPixels = isFillablePDFForm && designHeight
      ? approxInputAreaHeight * (lineHeight / 100)
      : undefined;

    /**
     * <----------------------------NOTICE------------------------->
     * Calculated here, from `changes`, which is the live value as it is being typed. This
     * component keeps typed text in local state until blur, so a parent side calculation
     * would read the last saved value and the font would not resize until the user clicked
     * away.
     */
    const shrinkFactor = (!autoShrinkEnabled || isExporterExpandable || !designWidth || !designHeight) ? 1 : calculateShrinkFactor({
      text: changes,
      designWidth,
      designHeight,
      baseFontSize: fontSize,
      maxLines: effectiveMaxLines,
      maxLength: autoShrinkMaxLength,
      fontFamily,
      fontWeight,
      fontStyle,
      textTransform,
      lineHeightRatio: lineHeightPixels ? (lineHeightPixels / fontSize) : 1.2,
      padding,

      /**
       * The display node is box-sizing: border-box, so its border sits inside the drawn
       * height rather than outside it. Without this the calculation believes it has more
       * room than the browser will give it.
       */
      borderWidth,
      reservedHeight,

      /**
       * With auto-height on the box is about to grow, so the height is an outcome rather than a
       * constraint and the font is decided by maxLines, maxLength and the width alone. Leaving
       * the height rule in would let the font creep back up after every growth and the field
       * would grow again on the next blur.
       */
      ignoreHeight: isAutoHeight,
    });

    //With auto-shrink off this is exactly the size the user set
    const effectiveFontSize = Math.max(1, Math.round(fontSize * shrinkFactor));

    /**
     * Tighten the line limit to what the box can really show.
     *
     * This happens after the shrink calculation rather than before it on purpose. The
     * calculation is asked to fit the designer's limit, and only once it has settled on a
     * size do we know the line height, which is what decides how many lines there is room
     * for. Doing it the other way round would need the answer before the question.
     *
     * maxLines on its own knows nothing about the height it has to fit into, and the clamp
     * limits lines rather than pixels, so a limit of five in a box with room for two produced
     * a block taller than the field and the text was cut half way through a row of letters.
     *
     * Left untouched in an export, where the field grows to fit its text instead. That covers
     * both exporter modes: on a float page the box is about to be grown from the overflow this
     * render exposes, and a clamp would hide the very lines being measured.
     */
    if (autoShrinkEnabled && !isExporterExpandable && !isExporterAutoHeight) {
      effectiveMaxLines = getFittingMaxLines({

        /**
         * With auto-height on there is no line limit to enforce: whatever the value needs, the
         * box is about to be made tall enough for. Infinity leaves the box as the only thing
         * tightening the clamp, which is what getFittingMaxLines then works out.
         */
        maxLines: isAutoHeight ? Infinity : effectiveMaxLines,
        designHeight,
        padding,
        borderWidth,
        reservedHeight,

        //On a fillable PDF the pitch is set by the printed rules, otherwise it follows the font
        lineHeightPixels: lineHeightPixels || getLineHeightPixels(effectiveFontSize),
      });
    }

    /**
     * One pixel line height shared by the editor and the display.
     *
     * On a fillable PDF lineHeightPixels is already the ruled pitch and both nodes measure it
     * themselves. Everywhere else it is undefined, textareaStyles resolves to
     * line-height: initial, and the focused textarea and the blurred display then disagree
     * about how tall a line is. Auto-height cannot live with that: the number of lines measured
     * on blur has to be the number of lines the reader is left looking at.
     */
    const resolvedLineHeightPixels = lineHeightPixels || (isAutoHeight ? getLineHeightPixels(effectiveFontSize) : undefined);

    /**
     * Which of the three nodes renders. Declared before its first use below, because a const
     * cannot be read above its own declaration.
     */
    const isAutoShrinkDisplay = !!autoShrinkEnabled && (!focused || disabled) && !isExporterExpandable;

    /**
     * Three way choice instead of the previous two way one. Because the component type
     * changes across the swap, React always unmounts and mounts a fresh element rather than
     * reusing one, which is what makes autoFocus reliable below.
     */
    let TextareaNode;
    if (isAutoShrinkDisplay) TextareaNode = AutoShrinkTextareaDisplay;
    else if (isFillablePDFForm) TextareaNode = FillablePDFTextarea;
    else TextareaNode = StandardTextarea;

    /**
     * Props for the display node only, and it matters that they never reach the other two.
     * StandardTextarea and FillablePDFTextarea both spread their props onto a styled DOM
     * element, and destructuring a name inside them does not remove it from this.props, so
     * anything passed in is forwarded. @emotion/is-prop-valid waves through anything shaped
     * like an event handler, so onRequestFocus would land on a real textarea and warn.
     */
    const displayOnlyProps = !isAutoShrinkDisplay ? {} : {

      //Tells the display node whether to render the plain shape or the layered PDF one
      isFillablePDFForm,

      /**
       * Lets the display node ask to become editable. It has to be a prop rather than
       * something the display node does itself, because `focused` lives in this component's
       * state and only this component can change it.
       */
      onRequestFocus: () => {
        if (!focused) this.setState({ focused: true });
      },

    };

    /**
     * Setting focused to true only swaps the display node for a real textarea. Nothing then
     * focuses that textarea, because it is a new element that did not exist when the click or
     * tab happened, and focus does not carry across a swap. Without this a click needs a
     * second click and a tab drops focus out of the form entirely.
     *
     * autoFocus alone is sufficient, because the swap always changes the component type so
     * React mounts a fresh element, which is exactly when autoFocus is honoured. It is a
     * genuine DOM attribute and safe to pass to all three nodes; the guard keeps it false for
     * every ordinary textarea field, which would otherwise steal focus on mount.
     */
    const shouldAutoFocus = !!autoShrinkEnabled && focused && !disabled;

    return (
      <StyledWrapper
        /**
         * Unpinned (height: auto) only while the exporter is measuring, which is the pass that
         * supplies onMeasureCompleted. In the final pass the grid slot has already been sized
         * to hold the value (grown to fit, or floored at the drawn height), so the wrapper fills
         * that slot the way it does in the builder. Left unpinned there, a short value in a tall
         * box drew a box the size of its text inside an empty slot.
         */
        {...{ margin, measure: isExporterExpandable && hasPopulatedValue && !!onMeasureCompleted }}
        ref={(e) => {
          if (e && !wrapperRef) this.setState({ wrapperRef: e });
        }}
      >
        <StyledFieldHeader $empty={isFieldHeaderEmpty({ title, titleDisplay, decorators, fieldMode })}>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
            }}
          />
          <FieldDecorators
            decorators={decorators}
            fieldMode={fieldMode}
            fieldId={fieldId}
            fieldIdentifier={fieldIdentifier}
            fieldPositionId={fieldPositionId}
            onDecoratorClick={onDecoratorClick}
          />
        </StyledFieldHeader>
        {this.getTooltip()}
        <TextareaNode
          /**
           * IMPORTANT: this prop is what makes StandardTextarea swap its real <textarea>
           * for a <span>. A textarea scrolls its overflow at a fixed height; a span reflows
           * and grows, which is both how the height gets discovered and how the finished
           * document shows every line. Editing is traded away in exchange, which costs
           * nothing because the exporter renders every field disabled anyway.
           *
           * It must stay on isExporterExpandable rather than any measurement-only flag.
           * The final render pass is not a measuring pass, and pointing this at one would
           * put a real textarea back in the finished document and clip the value again.
           */
          measure={isExporterExpandable}
          tabIndex={tabIndex}
          value={changes}
          autoFocus={shouldAutoFocus}
          {...displayOnlyProps}
          {...{

            id: fieldPositionId,
            fieldId,
            disabled,
            fieldMode,
            hideLines: disabled || !isFillablePDFForm || fieldMode !== FieldModes.edit,
            height,

            /**
             * Only set when auto-shrink is on; the two existing nodes ignore them. In a float
             * page export the shrink above still worked from effectiveMaxLines, but the drawn
             * clamp is lifted: the box is grown to fit instead.
             */
            maxLines: isExporterAutoHeight ? undefined : effectiveMaxLines,
            lineHeightPixels: resolvedLineHeightPixels,

            /**
             * Auto-height only, real textarea nodes only. A textarea keeps its intrinsic minimum
             * (two rows) as a flex item whatever its height says, so in a one-line box it would
             * overhang the slot and have its bottom border clipped. One row makes the intrinsic
             * minimum exactly the one line the box is sized for. The display div ignores it.
             */
            rows: (isAutoHeight && !isAutoShrinkDisplay) ? 1 : undefined,

            primaryColor,

            //The shrunken size. With auto-shrink off this equals fontSize exactly.
            fontSize: effectiveFontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
            lineHeight,
            tipVisible,
            tipTitle,
            tipDescription,
            fieldPresentationMode,
            pageLayoutMode,
          }}
          onChange={(e) => {
            this.setState({ changes: e.target.value });
          }}
          onClick={() => {
            if (!focused) this.setState({ focused: true });
          }}
          onFocus={(e) => {
            if (!disabled && onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
          }}
          onBlur={(e) => {
            const nextValue = e.target.value;

            /**
             * Read first, while the element is still on screen. e.target is the real textarea
             * for both StandardTextarea and FillablePDFTextarea, and a moment later focused is
             * false and it has been swapped for the display node.
             *
             * Measured on every blur, not only when the value changed, so a field that was
             * already overflowing when it was clicked into still grows. Shrinking is allowed
             * only when this focus changed the value: that is what the request records.
             */
            let measurement = null;

            if (isAutoHeight && !disabled) {

              const request = this.openAutoHeightRequest(nextValue, value);

              measurement = measureAutoHeight(e.target, nextValue, FieldTypes.textarea, height, { edited: request.edited });

              if (measurement) measurement.generation = request.generation;

            }

            if (focused) this.setState({ focused: false });

            if (!disabled && onChange && nextValue !== value) onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);

            if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

            /**
             * Deferred to the next task, and it has to be. The value change above and this
             * height change both read the same document and write it back, so sent in the same
             * tick the second one would be built on the pre-value document and would drop what
             * the user just typed.
             *
             * setTimeout rather than requestAnimationFrame, which the measuring paths in this
             * codebase use: a browser does not run animation frames for a hidden tab, so a
             * growth measured just before the user switched tabs would sit unpersisted until
             * they came back. A zero timeout runs regardless and still lands after React has
             * flushed the value change.
             */
            if (measurement && onAutoHeightChange) {

              /**
               * One pending emission at a time. The browser can deliver two blur events for a
               * single loss of focus (it does for this editor when a click both selects the
               * field and focuses it), and two emissions built from one measurement would grow
               * the box twice. The later blur wins; nothing about the box has changed between
               * them. A retry scheduled by an earlier answer is superseded as well.
               */
              clearTimeout(this.autoHeightTimer);
              clearTimeout(this.autoHeightRetryTimer);
              this.autoHeightRetries = 0;

              this.autoHeightTimer = setTimeout(() => this.emitAutoHeight(measurement), 0);

            }

          }}

        />
      </StyledWrapper>
    );
  }

  getTooltip = () => {

    const { fieldId, tipTitle, tipDescription, tipVisible, disabled } = this.props;

    if (disabled) return null;

    return (
      <StyledTooltipWrapper>
        <Tooltip
          fieldId={fieldId}
          title={tipTitle}
          description={tipDescription}
          tipVisible={tipVisible}
        />
      </StyledTooltipWrapper>
    );

  };

}

const StyledTextareaFieldWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
`;

const textareaStyles = (props) => {

  let overflowY = props.textOverflow === FormStylesOptions.textOverflowEllipsis ? 'hidden' : 'auto';
  if (props.fieldMode === FieldModes.readOnly || props.fieldMode === FieldModes.pdf) overflowY = 'hidden';

  return `
    width: 100%;
    height: 100%;
    font-size:${props.fontSize}px !important;
    color: ${props.fontColor};
    font-style: ${props.fontStyle};
    font-weight: ${props.fontWeight};
    text-align: ${props.textAlign};
    text-transform: ${props.textTransform};
    text-decoration: ${props.textDecoration};
    ${props.textOverflow ? `text-overflow: ${props.textOverflow};` : ''}
    border-radius: ${props.borderRadius}px;
    box-sizing: border-box;
    border: ${props.border};
    padding:  ${props.padding}px;
    line-height: ${props.lineHeightPixels !== undefined ? `${props.lineHeightPixels}px` : 'initial'} !important;
    background-color: transparent;
    resize: none;
    transition: border 0.15s ease-in-out;
    z-index: 1;

    /*
      Without this line, when text wrapping is set to hide (textOverflow :ellipsis) scroll bar appears within textarea in readOnly mode
    */
    overflow-y: ${overflowY};

    &:focus, &:focus-visible {
      outline: unset;
      border: ${props.borderWidth}px solid ${props.primaryColor};
    }

    &:hover {
      ${props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.primaryColor}`};
    }

  `;
};

const StyledStandardTextarea = styled.textarea`
  ${(props) => textareaStyles(props)}
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius}px;
`;

const StyledStandardTextareaMeasureable = styled.span`
  ${(props) => textareaStyles(props)}
  white-space: pre-line;
  overflow-y: visible;
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius}px;
`;

/**
 * This component is used to render a text area for standard forms.
 */
class StandardTextarea extends PureComponent {

  render() {

    const { id, borderWidth, borderColor, value, measure, onRef } = this.props;

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const textareaNode = !measure ? (
      <StyledStandardTextarea
        ref={onRef}
        data-testid={`${id}-textarea-field`}
        {...this.props}
        border={border}
      />
    ) : (
      <StyledStandardTextareaMeasureable
        ref={onRef}
        data-testid={`${id}-textarea-field-mesureable`}
        {...this.props}
        border={border}
      >
        {value}
      </StyledStandardTextareaMeasureable>
    );

    return (
      <StyledTextareaFieldWrapper>
        {textareaNode}
      </StyledTextareaFieldWrapper>
    );

  }
}

const StyledFillablePDFTextarea = styled.textarea`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  ${(props) => textareaStyles(props)}
`;

const StyledFillablePDFTextareaMeasureable = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  ${(props) => textareaStyles(props)}
  white-space: pre-line;

  /*
   * No overflow-y: visible here. textareaStyles already sets overflow-y: hidden for the modes
   * this span renders in (pdf; the readOnly comparison above it names FieldModes.readOnly,
   * which does not exist, so on screen the field stays a real textarea), so a value taller
   * than the box is clipped at the box instead of spilling into the page below. Auto-height
   * measures its overflow with scrollHeight, which hidden does not change, and its final box is
   * grown to fit before this draws.
   */
`;

const StyledFillablePDFTextareaBackground = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius}px;
`;

/**
 * This components main purpose is to isolate the input
 * styles into one location and not require style updates
 * all over the place when we update it in the future.
 */
class FillablePDFTextarea extends PureComponent {

  constructor(props) {
    super(props);
    this.state = {
      lineHeightPixels: 0
    };
    this.resizeObserver = null;
  }

  componentDidUpdate(prevProps, prevState) {

    const { wrapperRef } = this.state;

    //The wrapper arrives through a ref callback one render after mount, the first chance to watch it
    if (wrapperRef && wrapperRef !== prevState.wrapperRef) this.observeWrapper(wrapperRef);

    this.syncLineHeightPixels();

  }

  componentWillUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /**
   * Re-derive the pitch whenever the wrapper actually changes size, not only when React
   * happens to re-render this component.
   *
   * <----------------------------NOTICE------------------------->
   * react-grid-layout animates a grid item's height over 200ms; height is in its stylesheet's
   * transition-property. When auto-height persists a new height, componentDidUpdate runs at the
   * very start of that animation and reads the OLD wrapper height against the NEW lineHeight
   * percentage, and nothing renders again once the animation has settled. The text was then
   * drawn at a pitch belonging to a box that no longer existed. Watching the element itself is
   * what makes the pitch follow the box, whatever caused the box to change.
   *
   * Screen only. The exporter lays a page out once and never resizes it, and it is out of
   * scope here, so in pdf mode nothing is observed and the behaviour is exactly as before.
   */
  observeWrapper = (node) => {

    const { fieldMode } = this.props;

    if (typeof ResizeObserver === 'undefined' || fieldMode === FieldModes.pdf) return;

    if (this.resizeObserver) this.resizeObserver.disconnect();

    this.resizeObserver = new ResizeObserver(() => this.syncLineHeightPixels());
    this.resizeObserver.observe(node);

  }

  syncLineHeightPixels = () => {

    const { lineHeight } = this.props;
    const { wrapperRef, lineHeightPixels } = this.state;

    if (!wrapperRef) return;

    const nextLineHeightPixels = wrapperRef.offsetHeight * (lineHeight / 100);

    //Only written when it changes, so the observer and componentDidUpdate can never loop
    if (nextLineHeightPixels !== lineHeightPixels) this.setState({ lineHeightPixels: nextLineHeightPixels });

  }

  render() {

    const { lineHeightPixels, wrapperRef } = this.state;

    const {
      id,
      hideLines,
      height,
      fieldId,
      lineHeight,
      borderWidth,
      borderColor,
      borderRadius,
      backgroundColor,
      fontColor,
      textOverflow,
      value,
      fieldMode,
      onRef
    } = this.props;

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const inputNode = (fieldMode === FieldModes.readOnly || fieldMode === FieldModes.pdf) && textOverflow !== FormStylesOptions.textOverflowEllipsis ? (
      <StyledFillablePDFTextareaMeasureable
        ref={onRef}
        data-testid={`${id}-textarea-field`}
        {...this.props}
        border={border}
        lineHeightPixels={lineHeightPixels}
      >
        {value}
      </StyledFillablePDFTextareaMeasureable>
    ) : (
      <StyledFillablePDFTextarea
        ref={onRef}
        data-testid={`${id}-textarea-field`}
        {...this.props}
        border={border}
        lineHeightPixels={lineHeightPixels}
      />
    );

    return (
      <StyledTextareaFieldWrapper
        ref={(e) => {
          if (e && !wrapperRef) {
            if (onRef) onRef(e);
            this.setState({ wrapperRef: e });
          }
        }}
      >
        <StyledFillablePDFTextareaBackground
          data-testid={`${id}-textarea-field-background`}
          {...{
            backgroundColor,
            borderRadius,
          }}
        />
        {hideLines ? null : <TextareaFieldLines {...{ id, fieldId, fontColor, height, lineHeight, borderRadius }} />}
        {inputNode}
      </StyledTextareaFieldWrapper>
    );

  }
}

const StyledTextareaFieldLinesWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${(props) => props.borderRadius}px;
  overflow: hidden;
`;

const StyledTextareaFieldLine = styled.div`
  height: ${(props) => (`${props.height}px`)};
  width: 100%;
  border-bottom: 1px solid ${(props) => props.fontColor};

  /*
   * Each rule keeps exactly the pitch it was given. The wrapper is a column flexbox, and when
   * round(100 / lineHeight) rules add up to slightly more than the box, which is the case for
   * any lineHeight that does not divide 100, flex would squeeze every rule a little to fit.
   * The text is drawn at the true pitch, so squeezed rules drift away from it. Letting the
   * last rule run past the box instead is fine: the wrapper already clips it.
   */
  flex-shrink: 0;
`;

class TextareaFieldLines extends PureComponent {

  constructor(props) {
    super(props);
    this.state = { lines: 0 };
    this.resizeObserver = null;
  }

  static propTypes = {
    id: PropTypes.string,
    fontColor: PropTypes.string,
    borderRadius: PropTypes.number,
    lineHeight: PropTypes.number,
    onRef: PropTypes.func //Only used for testing purposes
  }

  static defaultProps = {
    lineHeight: 10
  }

  componentDidUpdate(prevProps, prevState) {

    const { lineHeight, height } = this.props;
    const { wrapperRef } = this.state;

    //The wrapper arrives through a ref callback one render after mount, the first chance to watch it
    if (wrapperRef && wrapperRef !== prevState.wrapperRef) this.observeWrapper(wrapperRef);

    //A changed height or lineHeight prop always redraws, exactly as it always has
    this.syncLines(height !== prevProps.height || lineHeight !== prevProps.lineHeight);

  }

  componentWillUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /**
   * Redraw the rules whenever the wrapper actually changes size. Same reason as
   * FillablePDFTextarea.observeWrapper: react-grid-layout animates the item's height, so a
   * count taken in componentDidUpdate describes the box at the start of the animation, not the
   * one the reader ends up looking at. The rules are only ever drawn in edit mode, so this never
   * runs in the exporter.
   */
  observeWrapper = (node) => {

    if (typeof ResizeObserver === 'undefined') return;

    if (this.resizeObserver) this.resizeObserver.disconnect();

    this.resizeObserver = new ResizeObserver(() => this.syncLines(false));
    this.resizeObserver.observe(node);

  }

  syncLines = (force) => {

    const { lineHeight } = this.props;
    const { wrapperRef, lines, lineHeights } = this.state;

    if (!wrapperRef) return;

    const wrapperHeight = wrapperRef.offsetHeight;
    const nextLineHeights = wrapperHeight * (lineHeight / 100);
    const nextLines = Math.round(wrapperHeight / nextLineHeights);

    if (force || nextLines !== lines || nextLineHeights !== lineHeights) {
      this.setState({ lines: nextLines, lineHeights: nextLineHeights });
    }

  }

  render() {

    const { id, borderRadius, onRef } = this.props;
    const { wrapperRef, lines, lineHeights } = this.state;

    const lineNodes = [];

    for (let i = 0; i < lines; i++) {
      lineNodes.push(
        <StyledTextareaFieldLine
          key={i}
          data-testid={`${id}-textarea-field-lines-line-${i}`}
          fontColor={this.getFontColor()}
          height={lineHeights}
        />
      );
    }

    return (
      <StyledTextareaFieldLinesWrapper
        {...{ borderRadius }}
        ref={(e) => {
          if (e && !wrapperRef) {
            if (onRef) onRef(e);
            this.setState({ wrapperRef: e });
          }
        }}
        data-testid={`${id}-textarea-field-lines`}
      >
        {lineNodes}
      </StyledTextareaFieldLinesWrapper>
    );
  }

  getFontColor = () => {

    const { fontColor } = this.props;
    return hexToRGBA(fontColor, '0.3');

  }

}

/**
 * The read-only node shown when an auto-shrink textarea field is not being typed in.
 *
 * <----------------------------NOTICE------------------------->
 * This solves two separate problems, and both are easy to miss.
 *
 * 1. A div ignores a `value` prop. The node is handed its text as value={changes}, which is
 *    how a textarea expects to receive it, so wiring a styled div straight in would render an
 *    empty box on screen and an empty box in the export. The value is moved into the children
 *    position here, the same way StandardTextarea does for its measuring span.
 *
 * 2. The field's chrome is drawn by siblings, not by the input, so swapping only the input
 *    loses it. textareaStyles sets background-color: transparent and StyledStandardTextarea
 *    re-adds the real background and radius after it, so a display node built on
 *    textareaStyles alone has no background. And on a fillable PDF form FillablePDFTextarea
 *    renders a background panel and the ruled lines as absolutely positioned siblings, so
 *    replacing the textarea alone drops both. This component mirrors whichever shape it is
 *    standing in for.
 */
class AutoShrinkTextareaDisplay extends PureComponent {

  constructor(props) {
    super(props);
    this.state = { wrapperRef: null, wrapperHeight: null };
    this.resizeObserver = null;
  }

  componentDidUpdate(prevProps, prevState) {

    const { wrapperRef } = this.state;

    //The wrapper arrives through a ref callback one render after mount, the first chance to watch it
    if (wrapperRef && wrapperRef !== prevState.wrapperRef) this.observeWrapper(wrapperRef);

  }

  componentWillUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /**
   * The pitch below is read from the wrapper during render, so once the wrapper is drawn this
   * node has no reason of its own to render again when the box changes size. react-grid-layout
   * animates the item's height, so after an auto-height growth the box settles some 200ms after
   * the render that measured it. Keeping the measured height in state, written by an observer,
   * is what brings the pitch back in step with the box. Screen only, like FillablePDFTextarea.
   */
  observeWrapper = (node) => {

    const { fieldMode } = this.props;

    if (typeof ResizeObserver === 'undefined' || fieldMode === FieldModes.pdf) return;

    if (this.resizeObserver) this.resizeObserver.disconnect();

    this.resizeObserver = new ResizeObserver(() => {
      const { wrapperHeight } = this.state;
      const nextWrapperHeight = node.offsetHeight;
      if (nextWrapperHeight !== wrapperHeight) this.setState({ wrapperHeight: nextWrapperHeight });
    });

    this.resizeObserver.observe(node);

  }

  render() {

    const { wrapperRef, wrapperHeight } = this.state;

    /**
     * Everything that is behaviour rather than style is pulled out here so it can be handled
     * by hand and, more importantly, kept off the DOM node. @emotion/is-prop-valid returns
     * true for onRequestFocus, onRef and value, because it waves through anything shaped like
     * an event handler and value is a real attribute, so a blanket props spread would push
     * all three onto a real div and produce React warnings.
     *
     * onFocus is pulled out and deliberately not re-attached: the parent's focus notification
     * belongs to the real textarea that replaces this node, so it fires once with a live
     * element behind its blur handle. onRef is listed defensively; this component is not sent
     * one, since focus handling relies on autoFocus alone.
     */
    const {
      value,
      onRequestFocus,
      onRef,
      onFocus,
      onChange,
      onBlur,
      measure,
      onClick,
      id, fieldId, borderWidth, borderColor, borderRadius, backgroundColor,
      fontColor, height, lineHeight, hideLines, isFillablePDFForm,
      disabled, tabIndex,
      ...restProps
    } = this.props;

    /**
     * We join width and color to a single border style because chrome displays the border as
     * a two tone color if you use border-color and border-width individually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    /**
     * The ruled line pitch is a percentage of the input area, not of the whole field, and
     * TextareaFieldLines draws its rules from that same box. FillablePDFTextarea measures it
     * rather than deriving it from the height prop, and so does this, or the clamped text
     * drifts off the rules whenever a title is visible. Falls back to the design space
     * approximation on the first paint, before the ref has run.
     */
    const measuredWrapperHeight = wrapperHeight || (wrapperRef ? wrapperRef.offsetHeight : null);

    const measuredLineHeightPixels = (isFillablePDFForm && measuredWrapperHeight)
      ? measuredWrapperHeight * (lineHeight / 100)
      : restProps.lineHeightPixels;

    /**
     * Built from restProps, the styles, and never from this.props, so the behaviour props
     * pulled out above cannot reach the DOM. The five style props that were destructured for
     * local use are restored, because textareaStyles reads fontColor, borderRadius and
     * borderWidth, and the plain display node reads backgroundColor and borderRadius.
     */
    const displayProps = {

      ...restProps,

      border,
      borderWidth,
      borderColor,
      borderRadius,
      backgroundColor,
      fontColor,

      disabled,
      lineHeightPixels: measuredLineHeightPixels,

      'data-testid': `${id}-textarea-field`,

      //Read-only fields are skipped by the tab key; editable ones stay reachable
      tabIndex: disabled ? -1 : tabIndex,

      //The existing onClick already sets focused upstream, so it is forwarded unchanged
      onClick,

      /**
       * This handler does one job and deliberately does not call the parent onFocus. Calling
       * both fires the host app's focus callback twice for a single focus, once for a node
       * about to be unmounted, and hands over a blur handle bound to that dying element.
       */
      onFocus: () => {
        if (!disabled) onRequestFocus();
      },

    };

    /**
     * Shape one: a fillable PDF form. Mirrors FillablePDFTextarea's structure, with the
     * clamped display node standing in for the textarea. Getting this wrong loses the
     * background layer and the ruled lines.
     */
    if (isFillablePDFForm) {

      return (
        <StyledTextareaFieldWrapper
          ref={(e) => { if (e && !wrapperRef) this.setState({ wrapperRef: e }); }}
        >
          <StyledFillablePDFTextareaBackground
            data-testid={`${id}-textarea-field-background`}
            backgroundColor={backgroundColor}
            borderRadius={borderRadius}
          />
          {hideLines ? null : <TextareaFieldLines {...{ id, fieldId, fontColor, height, lineHeight, borderRadius }} />}
          <StyledAutoShrinkFillablePDFDisplay {...displayProps}>
            <StyledAutoShrinkTextareaDisplayText {...displayProps}>
              {value}
            </StyledAutoShrinkTextareaDisplayText>
          </StyledAutoShrinkFillablePDFDisplay>
        </StyledTextareaFieldWrapper>
      );

    }

    /**
     * Shape two: an ordinary textarea field. Mirrors StandardTextarea: the same positioning
     * wrapper, and a single node that paints its own background.
     */
    return (
      <StyledTextareaFieldWrapper>
        <StyledAutoShrinkTextareaDisplay {...displayProps}>
          <StyledAutoShrinkTextareaDisplayText {...displayProps}>
            {value}
          </StyledAutoShrinkTextareaDisplayText>
        </StyledAutoShrinkTextareaDisplay>
      </StyledTextareaFieldWrapper>
    );

  }

}

/**
 * The outer box for both display shapes: the standard textarea look, at the full height the
 * designer drew.
 *
 * <----------------------------NOTICE------------------------->
 * The line clamp deliberately does NOT live here, and putting it back would break the
 * feature. -webkit-line-clamp only truncates while the box is free to shrink to the number
 * of lines it is allowed. textareaStyles sets height: 100%, and once the box is stretched to
 * a designer-drawn height taller than those lines, the clamp stops truncating and the full
 * text is painted. The clamp therefore belongs on an inner node with automatic height, which
 * is the same arrangement the text field uses.
 */
const autoShrinkDisplayStyles = (props) => `

  ${textareaStyles(props)}

  /* Anything past the clamped inner node is hidden rather than escaping the field */
  overflow: hidden;

  cursor: pointer;
`;

/**
 * The text itself. Sized by its own content, so the clamp can do its job, while the outer
 * node above keeps the field at its drawn height.
 */
const StyledAutoShrinkTextareaDisplayText = styled.div`

  ${(props) => getAutoShrinkClampStyles({ maxLines: props.maxLines })}

  /**
   * Explicit, because for an ordinary textarea field textareaStyles resolves to
   * line-height: initial, which is browser dependent and would leave the clamp misaligned
   * with the field. On a fillable PDF form this is the ruled line pitch, so the clamped text
   * sits exactly on the printed rules.
   */
  line-height: ${(props) => (props.lineHeightPixels
    ? `${props.lineHeightPixels}px`
    : `${getLineHeightPixels(props.fontSize)}px`)} !important;
`;

/**
 * The ordinary textarea display node. Mirrors StyledStandardTextarea, including re-adding the
 * background and radius that textareaStyles sets to transparent.
 */
const StyledAutoShrinkTextareaDisplay = styled.div`
  ${(props) => autoShrinkDisplayStyles(props)}
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius}px;
`;

/**
 * The fillable PDF display node. Mirrors StyledFillablePDFTextareaMeasureable: absolutely
 * positioned over the background layer and the ruled lines, and deliberately without its own
 * background colour, because on this layout the background is painted by the sibling panel
 * underneath it.
 */
const StyledAutoShrinkFillablePDFDisplay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  ${(props) => autoShrinkDisplayStyles(props)}
`;

export { TextareaFieldLines, StandardTextarea, FillablePDFTextarea, AutoShrinkTextareaDisplay };

export default TextareaField;
